// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// All login logic: number entry, send code, resend cooldown, confirm code and the Firebase
// session. Uses Firebase Phone Authentication (utils/phoneAuth.ts) — Google sends the SMS.
// Guards against stale responses with a generation counter.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import type { ConfirmationResult } from "firebase/auth";
import { errorMessage } from "../data/welcome";
import { sendSmsCode } from "../utils/phoneAuth";
import { isValidIndianMobile, sanitizeNationalNumber } from "../utils/phone";
import useAuth from "./useAuth";

export type LoginStatus = "idle" | "sending" | "sent" | "verifying" | "success";

/** How long before "Resend" is offered (Firebase also throttles on its side). */
const RESEND_AFTER_SECONDS = 30;

/**
 * The SMS-OTP login flow: number -> code sent -> code confirmed -> Firebase session.
 * Owns all of its state so the UI components stay presentational.
 */
export default function useOtpLogin() {
  const { confirmSmsCode } = useAuth();
  const [nationalNumber, setNationalNumber] = useState("");
  const [status, setStatus] = useState<LoginStatus>("idle");
  const [hasSentCode, setHasSentCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // The pending Firebase confirmation for the number a code was last sent to.
  const confirmation = useRef<ConfirmationResult | null>(null);
  // Ignore replies to a request the visitor has since abandoned (e.g. "Change number").
  const generation = useRef(0);

  const secondsLeft = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;

  useEffect(() => {
    if (!resendAt || Date.now() >= resendAt) return;
    const timer = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= resendAt) clearInterval(timer);
    }, 500);
    return () => clearInterval(timer);
  }, [resendAt]);

  const updateNumber = useCallback((input: string) => {
    setNationalNumber(sanitizeNationalNumber(input));
    setError(null);
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    setNow(Date.now());
    setResendAt(Date.now() + seconds * 1000);
  }, []);

  /** Sends (or re-sends) the code. Resolves true when a code is on its way. */
  const sendCode = useCallback(async (): Promise<boolean> => {
    if (!isValidIndianMobile(nationalNumber)) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return false;
    }
    const mine = ++generation.current;
    setError(null);
    setStatus("sending");
    try {
      const pending = await sendSmsCode(nationalNumber);
      if (mine !== generation.current) return false;
      confirmation.current = pending;
      setHasSentCode(true);
      setStatus("sent");
      startCooldown(RESEND_AFTER_SECONDS);
      return true;
    } catch (caught) {
      if (mine !== generation.current) return false;
      setStatus(hasSentCode ? "sent" : "idle");
      setError(errorMessage(caught));
      return false;
    }
  }, [nationalNumber, hasSentCode, startCooldown]);

  /** Checks the code and signs in. Resolves true once the visitor has a session. */
  const verifyCode = useCallback(
    async (code: string): Promise<boolean> => {
      if (!/^[0-9]{6}$/.test(code)) {
        setError("Enter the 6-digit code from the SMS.");
        return false;
      }
      if (!confirmation.current) {
        setError("Request a new code first.");
        return false;
      }
      const mine = ++generation.current;
      setError(null);
      setStatus("verifying");
      try {
        await confirmSmsCode(confirmation.current, code);
        if (mine !== generation.current) return false; // abandoned (e.g. "Change number")
        setStatus("success");
        return true;
      } catch (caught) {
        if (mine !== generation.current) return false;
        setStatus("sent");
        setError(errorMessage(caught));
        return false;
      }
    },
    [confirmSmsCode],
  );

  /** Back to the number step, discarding the pending code state. */
  const changeNumber = useCallback(() => {
    generation.current++;
    confirmation.current = null;
    setHasSentCode(false);
    setStatus("idle");
    setResendAt(null);
    setError(null);
  }, []);

  return {
    nationalNumber,
    status,
    hasSentCode,
    error,
    secondsLeft,
    updateNumber,
    sendCode,
    verifyCode,
    changeNumber,
    clearError: () => setError(null),
  };
}
